import { animated, useSpring } from '@react-spring/web'
import { getSignupURL } from '@src/helpers/signupUrl'
import { Breakpoints } from '@src/hooks/useBreakpoint'
import { useWindowDimensions } from '@src/hooks/useWindowDimensions'
import classNames from 'classnames'
import { ReactElement, type ReactNode, useEffect, useRef, useState } from 'react'
import { Button } from '../button/Button.component'
import styles from './WebOnboardingIntro.module.scss'

export interface WebOnboardingIntroProps {
  heading: string
  cta: { text: string; url: string }[]
  hasBackground?: boolean
  isCard?: boolean
  compactButton?: boolean
  animation?: boolean
}

export function WebOnboardingIntro({
  hasBackground = true,
  isCard,
  heading,
  cta,
  compactButton,
  animation,
}: WebOnboardingIntroProps): ReactNode {
  const [sectionDimensions, setSectionDimensions] = useState({ width: 0, height: 0 })
  const [targetPositions, setTargetPositions] = useState([
    { x: -200, y: 0 }, // Circle 1 starts off left edge
    { x: 300, y: 0 }, // Circle 2 starts off the right edge
  ])

  const { width } = useWindowDimensions()
  const isMobile = width && width < Breakpoints.bpLarge
  const sectionRef = useRef<HTMLElement>(null)

  function generateRandomPosition(noiseFactor = 0): { x: number; y: number } {
    return {
      x: Math.random() * sectionDimensions.width - sectionDimensions.width / 2 + noiseFactor,
      y: Math.random() * sectionDimensions.height - sectionDimensions.height / 2 + noiseFactor,
    }
  }

  function getTargetPositions(): { x: number; y: number }[] {
    return [
      {
        x: -sectionDimensions.width / 2,
        y: Math.random() * sectionDimensions.height - sectionDimensions.height / 2,
      },
      {
        x: sectionDimensions.width / 2,
        y: Math.random() * sectionDimensions.height - sectionDimensions.height / 2 + 30, // Add noise
      },
    ]
  }

  function circleSpeed(target: { x: number; y: number }, index: number): number {
    const distance = Math.hypot(target.x, target.y)
    const slowThreshold = sectionDimensions.width / 2
    const baseSpeed = isMobile ? 4000 : distance > slowThreshold ? 8000 : 5000
    return baseSpeed + index * 500
  }

  useEffect((): void => {
    setTargetPositions(getTargetPositions())
  }, [sectionDimensions])

  function renderAnimatedCircle(animation: any, className: string, src: string): ReactElement {
    return (
      <animated.div style={{ ...animation, position: 'absolute' }}>
        <img className={className} src={src} alt="ellipse" aria-hidden={true} />
      </animated.div>
    )
  }

  useEffect(() => {
    function updateSectionDimensions(): void {
      if (sectionRef.current) {
        setSectionDimensions({
          width: sectionRef.current.offsetWidth,
          height: sectionRef.current.offsetHeight,
        })
      }
    }

    updateSectionDimensions()
    window.addEventListener('resize', updateSectionDimensions)

    return () => window.removeEventListener('resize', updateSectionDimensions)
  }, [])

  useEffect(() => {
    setTargetPositions([
      {
        x: -sectionDimensions.width / 2, // Left edge
        y: Math.random() * sectionDimensions.height - sectionDimensions.height / 2,
      },
      {
        x: sectionDimensions.width / 2, // Right edge
        y: Math.random() * sectionDimensions.height - sectionDimensions.height / 2,
      },
    ])
  }, [sectionDimensions])

  const animations = targetPositions.map((target, index) =>
    useSpring({
      to: target,
      config: { duration: circleSpeed(target, index) },
      onRest: (): void =>
        setTargetPositions(prev =>
          prev.map((position, i) => (i === index ? generateRandomPosition(i * 10) : position)),
        ),
    }),
  )

  return (
    <section
      ref={sectionRef}
      className={classNames(styles.WebOnboardingIntro, {
        [styles.WebOnboardingIntro___noBackground]: !hasBackground,
        [styles.WebOnboardingIntro___isCard]: isCard,
      })}
    >
      {animation && (
        <>
          {renderAnimatedCircle(
            animations[0],
            styles.WebOnboardingIntro__animatedCircle,
            '/icons/ellipse.svg',
          )}
          {renderAnimatedCircle(
            animations[1],
            styles.WebOnboardingIntro__animatedCircle2,
            '/icons/ellipse2.svg',
          )}
        </>
      )}

      {hasBackground && !animation && (
        <div className={styles.WebOnboardingIntro__dotContainer}>
          <span className={styles.WebOnboardingIntro__dot1}>
            <img src="/icons/ellipse.svg" alt="ellipse" aria-hidden={true} />
          </span>
        </div>
      )}

      <div className={styles.WebOnboardingIntro__content}>
        <h3 className={styles.WebOnboardingIntro__title}>{heading}</h3>
        <div
          className={classNames(styles.WebOnboardingIntro__button, {
            [styles.WebOnboardingIntro__button___compact]: compactButton,
          })}
        >
          <Button size="flexible" theme="brandPurple" href={cta[0]!.url || getSignupURL('').href}>
            {cta[0]!.text}
          </Button>
        </div>
      </div>

      {hasBackground && !animation && (
        <div className={styles.WebOnboardingIntro__dotContainer}>
          <span className={styles.WebOnboardingIntro__dot2}>
            <img src="/icons/ellipse2.svg" alt="ellipse" aria-hidden={true} />
          </span>
        </div>
      )}
    </section>
  )
}
